/**
 * Today data slice: the interface this feature's screens use + the live implementation over the US API
 * (core/http.ts → /api/v1/app/*). The fixture implementation (in-memory Lone Star tenant) is in ./fixture.ts, loaded
 * only in dev / VITE_FIXTURES builds. Shared entity reads (jobs, techs, techs/live) live in core/entities.ts.
 *
 * Paths:
 *   GET  /v1/app/analytics/summary?from&to          P7 (real: api/src/modules/analytics/routes.js) → money.revenue_cents
 *   GET  /v1/app/analytics/receptionist?from&to     P7/P4 (assumed) → {calls, ai_answered, staff_answered, …}
 *   GET  /v1/app/activity?limit=50                  P1 (assumed; no recent-events route yet) → {items: StreamEvent[]}
 *   GET  /v1/dispatch/board                         P5 (real: dispatch/routes.js board, at the API root) → {escalations:[…]}
 *   POST /v1/app/dispatch/escalations/:id/resolve   P5 (assumed; no resolve route yet) {action}
 *   GET  /v1/app/onboarding                         P1 (real: onboarding/routes.js) → {tenant, progress, steps}
 *   POST /v1/app/calls/bridge {tech_id, job_id?}    P4 (assumed; same as the dispatch feature)
 *   POST /v1/app/techs/:id/break                    P3/P5 (assumed; same as the dispatch feature)
 */
import { APP, type Http } from '@app/core/http';
import { orRollingOut } from '@app/core/live/dto';
import type { ISO, StreamEvent, UUID } from '@app/core/types';

export interface TodaySummary {
  /** Tenant-local YYYY-MM-DD. */
  date: string;
  /** null = the source is unavailable (not zero). */
  calls: number | null;
  by_ai: number | null;
  collected_cents: number | null;
}

/** One open dispatcher alert from P5's escalation inbox (`dispatch.escalations`). */
export interface InboxAlert { id: string; job_id: UUID | null; tech_id: UUID | null; kind: string; created_at: ISO; data: Record<string, unknown> }

export interface OnboardingStep { key: string; order: number; title: string; status: 'todo' | 'in_progress' | 'done' | 'skipped'; completed_at: ISO | null }
export interface OnboardingState { progress: { done: number; total: number }; steps: OnboardingStep[] }

export interface Api {
  summary(date: string): Promise<TodaySummary>;
  activity(limit?: number): Promise<StreamEvent[]>;
  alerts(): Promise<InboxAlert[]>;
  ackAlert(id: string, action: string, idempotencyKey: string): Promise<{ resolved: boolean; by: string | null }>;
  onboarding(): Promise<OnboardingState>;
  callTech(techId: string, jobId: string | null, idempotencyKey: string): Promise<{ call_id: string }>;
  markBreak(techId: string, idempotencyKey: string): Promise<void>;
}

const num = (v: unknown) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));

/** Normalise one activity row into the stream envelope (the feed renders both the same way). */
export function normEvent(r: any): StreamEvent | null {
  const type = r?.type ?? r?.event_type;
  if (typeof type !== 'string') return null;
  return {
    id: String(r.id ?? ''), type, entity: r.entity ?? '', entity_id: String(r.entity_id ?? ''), version: r.version ?? null,
    data: r.data ?? r.payload ?? {}, at: r.at ?? r.created_at ?? new Date().toISOString(),
  };
}

export const live = (http: Http): Api => ({
  summary: async (date) => {
    const q = { query: { from: date, to: date } };
    const [s, r] = await Promise.allSettled([http.get<any>(`${APP}/analytics/summary`, q), http.get<any>(`${APP}/analytics/receptionist`, q)]);
    const sum = s.status === 'fulfilled' ? s.value : null;
    const rec = r.status === 'fulfilled' ? r.value : null;
    if (!sum && !rec && s.status === 'rejected') throw s.reason;
    return {
      date,
      calls: num(rec?.calls ?? rec?.receptionist?.calls),
      by_ai: num(rec?.ai_answered ?? rec?.receptionist?.ai_answered),
      collected_cents: num(sum?.money?.revenue_cents),
    };
  },
  activity: (limit = 50) => http.get<any>(`${APP}/activity`, { query: { limit } })
    .then((r) => (Array.isArray(r) ? r : r?.items ?? r?.events ?? []).map(normEvent).filter(Boolean) as StreamEvent[]),
  alerts: () => http.get<any>('/v1/dispatch/board').then((r) => (Array.isArray(r?.escalations) ? r.escalations : [])
    .map((e: any): InboxAlert => ({ id: String(e.id), job_id: e.job_id ?? null, tech_id: e.tech_id ?? null, kind: String(e.kind ?? 'escalation'), created_at: e.created_at, data: e.data ?? {} }))),
  ackAlert: (id, action, idempotencyKey) => http.post<any>(`${APP}/dispatch/escalations/${encodeURIComponent(id)}/resolve`, { action }, { idempotencyKey })
    .then((r) => ({ resolved: true, by: r?.resolved_by_name ?? null }), orRollingOut('escalation_resolve')),
  onboarding: () => http.get<any>(`${APP}/onboarding`).then((r) => ({
    progress: { done: Number(r?.progress?.done) || 0, total: Number(r?.progress?.total) || 0 },
    steps: (Array.isArray(r?.steps) ? r.steps : []).map((s: any) => ({ key: s.key, order: Number(s.order) || 0, title: s.title ?? s.key, status: s.status ?? 'todo', completed_at: s.completed_at ?? null })),
  })),
  callTech: (techId, jobId, idempotencyKey) => http.post<any>(`${APP}/calls/bridge`, { tech_id: techId, ...(jobId ? { job_id: jobId } : {}) }, { idempotencyKey })
    .then((r) => ({ call_id: r?.call_id ?? r?.call?.id ?? '' }), orRollingOut('call_tech_bridge')),
  markBreak: (techId, idempotencyKey) => http.post<void>(`${APP}/techs/${encodeURIComponent(techId)}/break`, {}, { idempotencyKey }).then(() => undefined, orRollingOut('tech_break')),
});
